import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useDispatch, useSelector } from 'react-redux';
import { be1Fetch } from '../../config/api';
import { setRunStatus, setRunDurations } from '../../store/workflow/workflowSlice';
import { computeNodeRunStatus, computeNodeDurations, RUN_STATUSES, runStatusLabel } from './utils/runStatus';
import { formatDateTime, formatDuration } from '../../utils/formatters';
import i18n from '../../i18n';
import { useTranslation } from 'react-i18next';
import { fmtMemory, fmtTaskRate, fmtTaskRows, memoryTitle, TaskMemory, FirstFailure, RunLink } from '../../services/executionApi';
import RunDetailModal, { RunRef } from '../execution/RunDetailModal';
import FirstFailureBanner, { NoNodeFailureNote } from '../execution/FirstFailureBanner';

interface RunRow { dag_run_id: string; state: string; run_type?: string; start_date?: string | null; end_date?: string | null; conf?: any; scope?: { mode?: string } | null; handled_failure?: boolean }
interface TaskRow { task_id: string; state?: string | null; try_number?: number; duration?: number | null; out_of_scope?: boolean; reused_from?: string | null; memory?: TaskMemory | null; rows?: number | null; child?: RunLink | null }
/** 부분 실행 표시 — 무설치판만 실행 기록에 범위를 남긴다(Airflow 는 직전 실행 안에서 다시 돌린다) */
const scopeLabel = (s?: { mode?: string } | null) => (s?.mode === 'from' ? i18n.t('dialog:runHistory.scopeFrom') : s?.mode === 'only' ? i18n.t('dialog:runHistory.scopeOnly') : '');

interface Props {
  dagId: string;
  workflowName: string;
  /** 노드 id → task id (배포 응답 또는 미리보기 변환). 태스크 행에 노드 라벨을 붙이고 캔버스 표시에 쓴다 */
  taskMap: Record<string, string>;
  /** 특정 노드의 로그를 바로 열 때 */
  focusNodeId?: string | null;
  onClose: () => void;
}

const fmt = (iso?: string | null) => formatDateTime(iso);
const dur = (a?: string | null, b?: string | null) => (a && b ? formatDuration(Math.max(0, Math.round((new Date(b).getTime() - new Date(a).getTime()) / 1000))) : '-');
const STATE_CLS: Record<string, string> = { success: 'text-green-600', failed: 'text-red-600', running: 'text-blue-600', queued: 'text-gray-500', skipped: 'text-amber-700', upstream_failed: 'text-red-500' };

/**
 * 실행 이력 — 왼쪽: 워크플로(DAG)의 run 목록(최근순), 가운데: 선택한 run 의 태스크(노드)별 상태·소요, 오른쪽: 태스크 로그.
 * "캔버스에 표시" 는 그 run 의 상태를 다이어그램 노드에 뱃지로 반영한다.
 */
const RunHistoryDialog: React.FC<Props> = ({ dagId, workflowName, taskMap, focusNodeId, onClose }) => {
  const { t } = useTranslation();
  useEscapeKey(onClose);
  const dispatch = useDispatch();
  const nodes = useSelector((s: any) => s.workflow.nodes);
  const edges = useSelector((s: any) => s.workflow.edges);
  const [runs, setRuns] = useState<RunRow[]>([]);
  const [runsErr, setRunsErr] = useState('');
  const [sel, setSel] = useState<string | null>(null);
  const [detail, setDetail] = useState<{ state?: string; tasks: TaskRow[]; firstFailure?: FirstFailure | null; parent?: RunLink | null } | null>(null);
  /** 부모·자식 실행을 보는 창 (실행 연결 고리) */
  const [viewing, setViewing] = useState<RunRef | null>(null);
  const [selTask, setSelTask] = useState<string | null>(null);
  const [log, setLog] = useState<{ text: string; loading: boolean; err?: string }>({ text: '', loading: false });
  const [autoRefresh, setAutoRefresh] = useState(true);

  const nodeByTask = useMemo(() => {
    const m: Record<string, any> = {};
    Object.entries(taskMap).forEach(([nid, tid]) => { const n = nodes.find((x: any) => x.id === nid); if (n) m[tid] = n; });
    return m;
  }, [taskMap, nodes]);
  const labelOf = (tid: string) => nodeByTask[tid]?.data?.label || tid;

  const loadRuns = useCallback(async () => {
    try {
      const r = await be1Fetch(`/api/canvas/${dagId}/runs?limit=30`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json(); setRuns(j.runs || []); setRunsErr('');
      return (j.runs || []) as RunRow[];
    } catch (e: any) { setRunsErr(t('dialog:runHistory.loadFailed', { message: e.message })); return []; }
  }, [dagId, t]);

  const loadDetail = useCallback(async (runId: string) => {
    try {
      const r = await be1Fetch(`/api/canvas/${dagId}/runs/${encodeURIComponent(runId)}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json(); setDetail({ state: j.state, tasks: j.tasks || [], firstFailure: j.first_failure || null, parent: j.parent || null });
    } catch (e: any) { setDetail({ tasks: [] }); }
  }, [dagId]);

  const loadLog = useCallback(async (runId: string, task: TaskRow) => {
    setLog({ text: '', loading: true });
    try {
      const r = await be1Fetch(`/api/canvas/${dagId}/runs/${encodeURIComponent(runId)}/tasks/${encodeURIComponent(task.task_id)}/log?try_number=${Math.max(1, task.try_number || 1)}`);
      const j = await r.json();
      setLog({ text: j.log || j.detail || '', loading: false });
    } catch (e: any) { setLog({ text: '', loading: false, err: t('dialog:runHistory.logFailed', { message: e.message }) }); }
  }, [dagId, t]);

  // 최초: 이력 → 최신 run 선택 → (focusNodeId 면) 그 노드의 태스크 로그
  useEffect(() => {
    (async () => {
      const rs = await loadRuns();
      if (rs.length) setSel(rs[0].dag_run_id);
    })();
  }, [loadRuns]);
  useEffect(() => { if (sel) { setSelTask(null); setLog({ text: '', loading: false }); loadDetail(sel); } }, [sel, loadDetail]);
  useEffect(() => {
    if (!detail || !sel || selTask) return;
    if (focusNodeId && taskMap[focusNodeId]) {
      const tk = detail.tasks.find((x) => x.task_id === taskMap[focusNodeId]);
      if (tk) { setSelTask(tk.task_id); loadLog(sel, tk); }
    }
  }, [detail, sel, selTask, focusNodeId, taskMap, loadLog]);
  // 실행 중이면 3초마다 갱신
  useEffect(() => {
    if (!autoRefresh) return;
    const running = detail?.state === 'running' || detail?.state === 'queued' || runs.some((r) => r.state === 'running' || r.state === 'queued');
    if (!running) return;
    const timer = setInterval(() => { loadRuns(); if (sel) loadDetail(sel); }, 3000);
    return () => clearInterval(timer);
  }, [autoRefresh, detail?.state, runs, sel, loadRuns, loadDetail]);

  const showOnCanvas = () => {
    if (!detail) return;
    dispatch(setRunStatus(computeNodeRunStatus(nodes, edges, taskMap, detail.tasks, detail.state)));
    dispatch(setRunDurations(computeNodeDurations(taskMap, detail.tasks)));
  };
  const selRun = runs.find((r) => r.dag_run_id === sel);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-30" onMouseDown={onClose}>
      <div className="bg-white rounded shadow-lg w-[1180px] max-w-[96vw] h-[80vh] flex flex-col" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div>
            <div className="text-sm font-semibold text-gray-800">{t('dialog:runHistory.title', { name: workflowName })}</div>
            <div className="text-xs text-gray-500">{t('dialog:runHistory.recent', { dagId, n: runs.length })}{runsErr ? ` · ${runsErr}` : ''}</div>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <label className="flex items-center gap-1 text-gray-600"><input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />{t('dialog:runHistory.autoRefresh')}</label>
            <button className="px-2 py-1 rounded border border-gray-300 hover:bg-gray-100 text-gray-700" onClick={() => { loadRuns(); if (sel) loadDetail(sel); }}>{t('common:ui.refresh')}</button>
            <button className="px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50" disabled={!detail} onClick={showOnCanvas} title={t('dialog:runHistory.showOnCanvasTitle')}>{t('dialog:runHistory.showOnCanvas')}</button>
            <button className="px-2 py-1 rounded hover:bg-gray-100 text-gray-600" onClick={onClose} title={t('common:ui.close')}>✕</button>
          </div>
        </div>

        <div className="flex-1 min-h-0 grid grid-cols-[300px_360px_1fr] divide-x divide-gray-200">
          {/* run 목록 */}
          <div className="overflow-auto min-h-0">
            {runs.length === 0 ? <div className="p-4 text-xs text-gray-500">{runsErr || t('dialog:runHistory.empty')}</div> : (
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-gray-50"><tr className="text-left text-gray-500"><th className="px-2 py-1">{t('dialog:runHistory.start')}</th><th className="px-2 py-1">{t('dialog:runHistory.state')}</th><th className="px-2 py-1">{t('dialog:runHistory.duration')}</th></tr></thead>
                <tbody>
                  {runs.map((r) => (
                    <tr key={r.dag_run_id} className={`cursor-pointer border-b border-gray-100 hover:bg-gray-50 ${sel === r.dag_run_id ? 'bg-blue-50' : ''}`} onClick={() => setSel(r.dag_run_id)} title={r.dag_run_id}>
                      <td className="px-2 py-1 whitespace-nowrap text-gray-700">{fmt(r.start_date)}</td>
                      <td className={`px-2 py-1 font-medium ${STATE_CLS[r.state] || 'text-gray-600'}`}>{r.state}{r.handled_failure && r.state === 'success' && <span className="ml-1 rounded bg-amber-100 px-1 text-[0.625rem] font-normal text-amber-800" title={t('execution:handled.title')}>{t('execution:handled.short')}</span>}{r.scope && <span className="ml-1 rounded bg-indigo-50 px-1 text-[0.625rem] font-normal text-indigo-700">{scopeLabel(r.scope)}</span>}</td>
                      <td className="px-2 py-1 text-gray-500">{dur(r.start_date, r.end_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {/* 태스크(노드) 목록 */}
          <div className="overflow-auto min-h-0">
            {!sel ? <div className="p-4 text-xs text-gray-500">{t('dialog:runHistory.selectRun')}</div> : !detail ? <div className="p-4 text-xs text-gray-500">{t('common:ui.loading')}</div> : (
              <>
                <div className="px-3 py-2 text-xs text-gray-600 border-b border-gray-100">
                  <div className="truncate" title={sel}>run: {sel}</div>
                  <div>{t('dialog:runHistory.stateLine')} <b className={STATE_CLS[detail.state || ''] || ''}>{detail.state}</b> · {fmt(selRun?.start_date)} ~ {fmt(selRun?.end_date)}{selRun?.conf && Object.keys(selRun.conf).length ? ` · conf ${JSON.stringify(selRun.conf)}` : ''}</div>
                </div>
                {detail.parent && (
                  <div className="mx-2 my-1.5 px-2.5 py-1.5 rounded border border-blue-200 bg-blue-50 text-xs text-gray-700 flex items-center gap-2">
                    <span className="shrink-0 text-blue-700 font-semibold">{t('execution:detail.calledBy')}</span>
                    <span className="truncate">{detail.parent.name || detail.parent.dag_id} › {detail.parent.task_id}</span>
                    <button type="button" className="ml-auto shrink-0 text-blue-700 hover:underline"
                      onClick={() => detail.parent && setViewing({ dagId: detail.parent.dag_id, runId: detail.parent.run_id, name: detail.parent.name, taskId: detail.parent.task_id })}>
                      {t('execution:detail.openParent')}
                    </button>
                  </div>
                )}
                {!detail.firstFailure && detail.state === 'failed' && <NoNodeFailureNote />}
                {detail.firstFailure && (() => {
                  const ff = detail.firstFailure;
                  const tk = detail.tasks.find((x) => x.task_id === ff.task_id);
                  return <FirstFailureBanner failure={ff} label={labelOf(ff.task_id)} onOpenLog={tk ? () => { setSelTask(tk.task_id); loadLog(sel, tk); } : undefined} />;
                })()}
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-gray-50"><tr className="text-left text-gray-500"><th className="px-2 py-1">{t('dialog:runHistory.nodeTask')}</th><th className="px-2 py-1">{t('dialog:runHistory.state')}</th><th className="px-2 py-1">{t('dialog:runHistory.duration')}</th><th className="px-2 py-1 text-right" title={t('execution:rows.colTitle')}>{t('execution:rows.col')}</th><th className="px-2 py-1 text-right" title={t('execution:rows.rateTitle')}>{t('execution:rows.rateCol')}</th><th className="px-2 py-1 text-right">{t('execution:mem.col')}</th></tr></thead>
                  <tbody>
                    {detail.tasks.map((tk) => (
                      <tr key={tk.task_id} className={`cursor-pointer border-b border-gray-100 hover:bg-gray-50 ${selTask === tk.task_id ? 'bg-blue-50' : ''}`} onClick={() => { setSelTask(tk.task_id); loadLog(sel, tk); }}>
                        <td className="px-2 py-1"><div className="text-gray-800">{labelOf(tk.task_id)}</div><div className="text-[0.625rem] text-gray-400 font-mono">{tk.task_id}</div>
                          {tk.child && (
                            <button type="button" className="text-[0.6875rem] text-blue-700 hover:underline text-left"
                              onClick={(e) => { e.stopPropagation(); tk.child && setViewing({ dagId: tk.child.dag_id, runId: tk.child.run_id, name: tk.child.name }); }}>
                              {t('execution:detail.openChild', { name: tk.child.name || tk.child.dag_id })}
                            </button>
                          )}
                        </td>
                        {tk.out_of_scope ? <td className="px-2 py-1 text-gray-400" title={t('dialog:runHistory.outOfScopeTitle')}>{t('dialog:runHistory.outOfScope')}</td>
                          : <td className={`px-2 py-1 font-medium ${STATE_CLS[tk.state || ''] || 'text-gray-500'}`}>{tk.state || '-'}{tk.reused_from && <div className="text-[0.625rem] font-normal text-indigo-600" title={t('dialog:runHistory.reusedTitle', { run: tk.reused_from })}>{t('dialog:runHistory.reused')}</div>}</td>}
                        <td className="px-2 py-1 text-gray-500">{tk.out_of_scope || tk.reused_from ? '-' : tk.duration != null ? formatDuration(Math.round(tk.duration)) : '-'}</td>
                        <td className="px-2 py-1 text-right text-gray-500 tabular-nums">{tk.out_of_scope || tk.reused_from ? '-' : fmtTaskRows(tk)}</td>
                        <td className="px-2 py-1 text-right text-gray-500 tabular-nums">{tk.out_of_scope || tk.reused_from ? '-' : fmtTaskRate(tk)}</td>
                        <td className="px-2 py-1 text-right text-gray-500 tabular-nums" title={memoryTitle(tk.memory)}>{tk.out_of_scope || tk.reused_from ? '-' : fmtMemory(tk.memory)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
          {/* 로그 */}
          <div className="min-w-0 min-h-0 flex flex-col overflow-hidden">
            <div className="px-3 py-2 text-xs text-gray-600 border-b border-gray-100 flex items-center justify-between">
              <span>{selTask ? <>{t('dialog:runHistory.logOf')}<b>{labelOf(selTask)}</b> <span className="font-mono text-gray-400">{selTask}</span></> : t('dialog:runHistory.selectTask')}</span>
              {selTask && sel && <button className="px-2 py-0.5 rounded border border-gray-300 hover:bg-gray-100" onClick={() => { const tk = detail?.tasks.find((x) => x.task_id === selTask); if (tk) loadLog(sel, tk); }}>{t('dialog:runHistory.reload')}</button>}
            </div>
            <pre className="flex-1 min-h-0 overflow-auto m-0 p-3 text-[0.6875rem] leading-4 font-mono whitespace-pre-wrap bg-gray-50 text-gray-800">
              {log.loading ? t('dialog:runHistory.readingLog') : log.err ? log.err : log.text || (selTask ? t('dialog:runHistory.noLog') : '')}
            </pre>
          </div>
        </div>
        <div className="px-4 py-1.5 border-t border-gray-200 text-[0.6875rem] text-gray-500">
          {t('dialog:runHistory.legend', { states: RUN_STATUSES.map((k) => `${k}=${runStatusLabel(k)}`).join(' · ') })}
        </div>
      </div>
      {viewing && <RunDetailModal start={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
};

export default RunHistoryDialog;
